/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

import { useState } from "react";

const NAVY = "#0f1f3d";
const NAVY_LIGHT = "#162848";
const NAVY_MID = "#1e3560";
const ORANGE = "#F97316";
// eslint-disable-next-line no-unused-vars
const ORANGE_LIGHT = "#ffedd5";
// eslint-disable-next-line no-unused-vars
const ORANGE_DIM = "#c2410c";
const TEAL = "#0891b2";
const GREEN = "#16a34a";
const PURPLE = "#7c3aed";
const AMBER = "#d97706";
const BORDER = "#1e2d4a";
const SURFACE = "#111827";
const GRAY = "#6b7280";

// ─── SHARED COMPONENTS ───────────────────────────────────────────

function DeviceCard({ icon, label, sub, color, active, onClick, small }) {
  return (
    <div onClick={onClick} style={{
      background: active ? color + "22" : NAVY_LIGHT,
      border: `2px solid ${active ? color : BORDER}`,
      borderRadius: "10px",
      padding: small ? "8px 10px" : "12px 14px",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.15s",
      textAlign: "center",
      minWidth: small ? "90px" : "110px",
    }}>
      <div style={{ fontSize: small ? "20px" : "24px", marginBottom: "4px" }}>{icon}</div>
      <div style={{ fontSize: small ? "10px" : "11px", fontWeight: 700, color: active ? color : "#c0cce0", lineHeight: 1.2 }}>{label}</div>
      {sub && <div style={{ fontSize: "9px", color: GRAY, marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function BusLine({ color = ORANGE, vertical, length = 200, label }) {
  return (
    <div style={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{
        width: vertical ? "4px" : `${length}px`,
        height: vertical ? `${length}px` : "4px",
        background: `linear-gradient(${vertical ? "180deg" : "90deg"}, ${color}80, ${color}, ${color}80)`,
        borderRadius: "2px",
        boxShadow: `0 0 8px ${color}40`,
      }} />
      {label && (
        <div style={{
          position: "absolute",
          top: vertical ? "50%" : "-18px",
          left: vertical ? "10px" : "50%",
          transform: vertical ? "translateY(-50%)" : "translateX(-50%)",
          fontSize: "9px", color, fontWeight: 700,
          letterSpacing: "0.5px", whiteSpace: "nowrap",
          background: SURFACE, padding: "1px 4px", borderRadius: "3px",
        }}>{label}</div>
      )}
    </div>
  );
}

function InfoPanel({ device, onClose }) {
  if (!device) return null;
  return (
    <div style={{
      position: "fixed", top: "50%", left: "50%",
      transform: "translate(-50%, -50%)",
      background: NAVY, border: `2px solid ${ORANGE}`,
      borderRadius: "14px", padding: "24px",
      width: "320px", zIndex: 1000,
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
        <div>
          <div style={{ fontSize: "22px", marginBottom: "4px" }}>{device.icon}</div>
          <div style={{ fontWeight: 800, fontSize: "16px", color: "#f0f4ff" }}>{device.label}</div>
          {device.model && <div style={{ fontSize: "11px", color: ORANGE, marginTop: "2px", fontWeight: 600 }}>{device.model}</div>}
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: GRAY, cursor: "pointer", fontSize: "18px" }}>✕</button>
      </div>
      <div style={{ fontSize: "12px", color: "#94a3b8", lineHeight: 1.6, marginBottom: "12px" }}>{device.desc}</div>
      {device.specs && (
        <div style={{ background: NAVY_MID, borderRadius: "8px", padding: "10px 12px" }}>
          {device.specs.map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
              <span style={{ fontSize: "10px", color: GRAY }}>{s[0]}</span>
              <span style={{ fontSize: "10px", color: "#c0cce0", fontWeight: 600 }}>{s[1]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── RESIDENTIAL DIAGRAM ─────────────────────────────────────────

const residentialDevices = {
  psu: {
    icon: "⚡", label: "KNX Power Supply", model: "GVS KNX PSU 160mA",
    desc: "Provides 29V DC bus power to all KNX TP devices on the line. Every KNX installation requires at least one PSU per line segment.",
    specs: [["Output", "29V DC SELV"], ["Current", "160-640mA"], ["Max devices", "64 per segment"], ["Bus cable", "YCYM 2×2×0.8mm²"]]
  },
  router: {
    icon: "🌐", label: "IP Router/Gateway", model: "KNX IP Router",
    desc: "Connects the KNX TP bus to the IP network. Enables remote ETS access, visualization apps, and integration with smart home platforms.",
    specs: [["Protocol", "KNXnet/IP"], ["ETS", "ETS5/6 compatible"], ["Access", "Remote via app"], ["Integration", "Home Assistant, Loxone"]]
  },
  touchpad: {
    icon: "📱", label: "Waltz Touch+ Pad", model: "GVS KNX Waltz Touch+ (KNX Secure)",
    desc: "Premium KNX Secure touch panel for lighting, HVAC, blinds and scene control. Available in multiple colors to match interior design.",
    specs: [["Protocol", "KNX Secure"], ["Functions", "Light, HVAC, Blinds"], ["Display", "Capacitive touch"], ["Colors", "5 color options"]]
  },
  pushbutton: {
    icon: "🔘", label: "Push Button Sensor", model: "GVS KNX Push Button with Display",
    desc: "Wall-mounted KNX push button with integrated display. 1/2/3/4-gang options. Each button independently programmable via ETS6.",
    specs: [["Gangs", "1/2/3/4-gang"], ["Display", "LED status indicators"], ["Functions", "Switch, dim, blind, scene"], ["Mounting", "Standard 55mm box"]]
  },
  dimmer: {
    icon: "💡", label: "Dimmer Actuator", model: "GVS 4CH Universal Dimming Actuator",
    desc: "4-channel universal dimming actuator for LED, halogen and fluorescent loads. DIN rail mounted. ETL certification in progress for NA.",
    specs: [["Channels", "4 independent"], ["Load types", "LED, halogen, CFL"], ["Max load", "250W per channel"], ["Certification", "ETL in progress"]]
  },
  switching: {
    icon: "🔌", label: "Switching Actuator", model: "GVS 8CH Multifunction Actuator",
    desc: "8-channel switching actuator for lighting circuits, sockets and general loads. Logic functions, timers and scene control built in.",
    specs: [["Channels", "8 independent"], ["Max current", "16A per channel"], ["Functions", "Switch, timer, logic"], ["Certification", "ETL in progress"]]
  },
  blind: {
    icon: "🪟", label: "Blind Actuator", model: "KNX Blind/Shutter Actuator",
    desc: "Controls motorized blinds, shutters and curtains. Automatic sun protection via integration with weather sensors.",
    specs: [["Channels", "4×2 outputs"], ["Safety", "Wind/rain auto-stop"], ["Functions", "Position, tilt, scene"], ["Travel time", "Auto-calibration"]]
  },
  sensor: {
    icon: "🌡️", label: "Multifunction Sensor", model: "GVS KNX Multifunction 9-in-1 (KNX Secure)",
    desc: "All-in-one room sensor measuring temperature, humidity, CO₂, brightness, presence and more. Ideal for HVAC and lighting automation.",
    specs: [["Measurements", "9 parameters"], ["CO₂ range", "0-2000ppm"], ["Presence", "PIR + microwave"], ["Protocol", "KNX Secure"]]
  },
  thermostat: {
    icon: "🌡", label: "Thermostat Lite", model: "GVS KNX Thermostat Lite 55mm",
    desc: "Compact KNX room thermostat for heating and cooling control. Setpoint adjustment, operating mode selection and floor heating support.",
    specs: [["Display", "LCD with backlight"], ["Control", "Heating & cooling"], ["Setpoint", "±5°C adjustment"], ["Mounting", "55mm standard box"]]
  },
  tablet: {
    icon: "📟", label: "Smart Touch V50s", model: "GVS KNX Smart Touch V50s",
    desc: "7-inch KNX touch panel with video intercom capability. Full home control visualization, door camera integration and scene management.",
    specs: [["Display", "7\" capacitive touch"], ["Intercom", "Video door station"], ["Resolution", "1024×600px"], ["Integration", "Full KNX + IP cam"]]
  },
};

function ResidentialDiagram() {
  const [active, setActive] = useState(null);
  const [view, setView] = useState("interactive");
  const toggle = (id) => setActive(active === id ? null : id);

  const rooms = [
    { label: "Living Room", color: TEAL, devices: ["touchpad", "dimmer", "blind", "sensor"] },
    { label: "Bedroom", color: PURPLE, devices: ["pushbutton", "switching", "thermostat"] },
    { label: "Kitchen", color: AMBER, devices: ["pushbutton", "switching"] },
    { label: "Entrance", color: GREEN, devices: ["tablet", "pushbutton"] },
  ];

  const icons = { touchpad: "📱", pushbutton: "🔘", dimmer: "💡", switching: "🔌", blind: "🪟", sensor: "🌡️", thermostat: "🌡", tablet: "📟" };
  const labels = { touchpad: "Waltz Touch+", pushbutton: "Push Button", dimmer: "Dimmer 4CH", switching: "Switch 8CH", blind: "Blind Act.", sensor: "9-in-1 Sensor", thermostat: "Thermostat", tablet: "V50s Panel" };

  return (
    <div>
      <div style={{ display:"flex", gap:"8px", marginBottom:"14px" }}>
        {["interactive","schematic"].map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            padding:"6px 16px", borderRadius:"6px", fontSize:"12px", fontWeight: view===v ? 700 : 500,
            background: view===v ? "#F97316" : "#1e3560",
            color: view===v ? "#fff" : "#94a3b8",
            border: `1px solid ${view===v ? "#F97316" : "#253d6e"}`,
            cursor:"pointer", textTransform:"capitalize",
          }}>{v}</button>
        ))}
      </div>
      {view === "interactive" && (
      <>
      {/* System backbone */}
      <div style={{
        background: NAVY_MID, border: `1px solid ${BORDER}`,
        borderRadius: "12px", padding: "16px 20px",
        marginBottom: "20px",
        display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap",
      }}>
        <div style={{ fontSize: "11px", color: ORANGE, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginRight: "8px" }}>System Core</div>
        {["psu", "router"].map(id => (
          <DeviceCard key={id} {...residentialDevices[id]} active={active === id} onClick={() => toggle(id)} small />
        ))}
        <div style={{ flex: 1, height: "4px", background: `linear-gradient(90deg, ${ORANGE}, ${ORANGE}40)`, borderRadius: "2px", minWidth: "40px", boxShadow: `0 0 8px ${ORANGE}40` }} />
        <div style={{ fontSize: "10px", color: ORANGE, fontWeight: 700 }}>KNX TP BUS — 29V DC</div>
      </div>

      {/* Room grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
        {rooms.map(room => (
          <div key={room.label} style={{
            background: NAVY_LIGHT, border: `1px solid ${room.color}40`,
            borderTop: `3px solid ${room.color}`,
            borderRadius: "10px", padding: "14px",
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: room.color, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "1px" }}>
              {room.label}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {room.devices.map(id => (
                <DeviceCard key={id}
                  icon={icons[id]} label={labels[id]}
                  color={room.color}
                  active={active === id}
                  onClick={() => toggle(id)}
                  small
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {active && <InfoPanel device={residentialDevices[active]} onClose={() => setActive(null)} />}
      {active && <div onClick={() => setActive(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999 }} />}
      </>
      )}
      {view === "schematic" && (
        <svg width="100%" viewBox="0 0 680 740" style={{display:"block"}}>
          <rect x="0" y="0" width="680" height="740" fill="#f1f5f9"/>
          <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </marker>
          </defs>
          {/* KNX TP Bus backbone */}
          <text x="340" y="30" textAnchor="middle" style={{fontSize:"13px",fontWeight:600,fill:"#0f172a"}}>KNX residential system — TP bus schematic</text>
          <text x="340" y="48" textAnchor="middle" style={{fontSize:"11px",fill:"#64748b"}}>29V DC SELV · IEC 14543-3 · ETS6</text>
          <text x="340" y="82" textAnchor="middle" style={{fontSize:"10px",fontWeight:700,fill:"#BA7517"}}>KNX TP bus — 29V DC</text>
          <line x1="60" y1="90" x2="620" y2="90" stroke="#BA7517" strokeWidth="3" strokeLinecap="round"/>
          {/* PSU */}
          <rect x="40" y="104" width="96" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="88" y="122" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">PSU 160mA</text>
          <text x="88" y="138" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">29V DC SELV</text>
          <line x1="88" y1="104" x2="88" y2="90" stroke="#BA7517" strokeWidth="2"/>
          {/* IP Router */}
          <rect x="160" y="104" width="96" height="44" rx="6" fill="#ffffff" stroke="#378ADD" strokeWidth="1.5"/>
          <text x="208" y="122" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">IP router</text>
          <text x="208" y="138" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">KNXnet/IP</text>
          <line x1="208" y1="104" x2="208" y2="90" stroke="#BA7517" strokeWidth="2"/>
          <text x="208" y="162" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}}>↑ ETS6 / remote app</text>
          {/* Living Room zone */}
          <rect x="40" y="196" width="290" height="200" rx="10" fill="none" stroke="#0F6E56" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="52" y="212" style={{fontSize:"10px",fontWeight:600,fill:"#0F6E56"}}>Living room</text>
          <line x1="100" y1="90" x2="100" y2="230" stroke="#BA7517" strokeWidth="1.5" strokeDasharray="4 2"/>
          <line x1="60" y1="230" x2="320" y2="230" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="52" y="244" width="88" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="96" y="262" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Waltz Touch+</text>
          <text x="96" y="278" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">KNX Secure</text>
          <line x1="96" y1="244" x2="96" y2="230" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="156" y="244" width="88" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="200" y="262" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Dimmer 4CH</text>
          <text x="200" y="278" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">250W/ch LED</text>
          <line x1="200" y1="244" x2="200" y2="230" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="256" y="244" width="108" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="310" y="262" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Blind act.</text>
          <text x="310" y="278" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">4×2 outputs</text>
          <line x1="310" y1="244" x2="310" y2="230" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="104" y="312" width="112" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="160" y="330" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">9-in-1 sensor</text>
          <text x="160" y="346" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">CO₂, presence, temp</text>
          <line x1="160" y1="312" x2="160" y2="230" stroke="#BA7517" strokeWidth="1.2" strokeDasharray="3 2"/>
          {/* Bedroom zone */}
          <rect x="350" y="196" width="290" height="200" rx="10" fill="none" stroke="#534AB7" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="362" y="212" style={{fontSize:"10px",fontWeight:600,fill:"#534AB7"}}>Bedroom</text>
          <line x1="480" y1="90" x2="480" y2="230" stroke="#BA7517" strokeWidth="1.5" strokeDasharray="4 2"/>
          <line x1="360" y1="230" x2="630" y2="230" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="362" y="244" width="88" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="406" y="262" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Push button</text>
          <text x="406" y="278" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">4-gang KNX</text>
          <line x1="406" y1="244" x2="406" y2="230" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="466" y="244" width="108" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="520" y="262" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Switch 8CH</text>
          <text x="520" y="278" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">16A/ch</text>
          <line x1="520" y1="244" x2="520" y2="230" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="564" y="244" width="116" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="622" y="262" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Thermostat</text>
          <text x="622" y="278" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Heating/cooling</text>
          <line x1="622" y1="244" x2="622" y2="230" stroke="#BA7517" strokeWidth="1.2"/>
          {/* Line 2 bus */}
          <line x1="340" y1="90" x2="340" y2="430" stroke="#BA7517" strokeWidth="1.5" strokeDasharray="4 2"/>
          <line x1="60" y1="430" x2="620" y2="430" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="340" y="422" textAnchor="middle" style={{fontSize:"10px",fontWeight:700,fill:"#BA7517"}}>KNX TP bus — line 2</text>
          {/* Kitchen zone */}
          <rect x="40" y="436" width="270" height="130" rx="10" fill="none" stroke="#854F0B" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="52" y="452" style={{fontSize:"10px",fontWeight:600,fill:"#854F0B"}}>Kitchen</text>
          <line x1="140" y1="430" x2="140" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          <line x1="60" y1="464" x2="300" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="56" y="478" width="88" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="100" y="496" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Push button</text>
          <text x="100" y="512" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">2-gang</text>
          <line x1="100" y1="478" x2="100" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="176" y="478" width="108" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="230" y="496" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Switch 8CH</text>
          <text x="230" y="512" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">16A/ch</text>
          <line x1="230" y1="478" x2="230" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          {/* Entrance zone */}
          <rect x="350" y="436" width="290" height="130" rx="10" fill="none" stroke="#0F6E56" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="362" y="452" style={{fontSize:"10px",fontWeight:600,fill:"#0F6E56"}}>Entrance</text>
          <line x1="500" y1="430" x2="500" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          <line x1="360" y1="464" x2="630" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="362" y="478" width="104" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="414" y="496" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">V50s panel</text>
          <text x="414" y="512" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">7" touch + intercom</text>
          <line x1="414" y1="478" x2="414" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="490" y="478" width="88" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="534" y="496" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Push button</text>
          <text x="534" y="512" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Door call</text>
          <line x1="534" y1="478" x2="534" y2="464" stroke="#BA7517" strokeWidth="1.2"/>
          {/* Legend */}
          <rect x="40" y="600" width="600" height="56" rx="8" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.5"/>
          <text x="56" y="622" style={{fontSize:"10px",fontWeight:700,fill:"#64748b"}}>Legend:</text>
          <line x1="110" y1="619" x2="144" y2="619" stroke="#BA7517" strokeWidth="2.5"/>
          <text x="150" y="623" style={{fontSize:"10px",fill:"#64748b"}}>KNX TP bus</text>
          <line x1="110" y1="640" x2="144" y2="640" stroke="#BA7517" strokeWidth="1.5" strokeDasharray="4 2"/>
          <text x="150" y="644" style={{fontSize:"10px",fill:"#64748b"}}>Spur / drop</text>
          <rect x="270" y="612" width="10" height="10" rx="2" fill="#1D9E75" stroke="#0F6E56"/>
          <text x="286" y="622" style={{fontSize:"10px",fill:"#64748b"}}>Sensors / displays</text>
          <rect x="270" y="632" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#BA7517"/>
          <text x="286" y="642" style={{fontSize:"10px",fill:"#64748b"}}>Actuators</text>
          <rect x="430" y="612" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#7c3aed"/>
          <text x="446" y="622" style={{fontSize:"10px",fill:"#64748b"}}>Input devices</text>
          <rect x="430" y="632" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#378ADD"/>
          <text x="446" y="642" style={{fontSize:"10px",fill:"#64748b"}}>Infrastructure</text>
        </svg>
      )}
    </div>
  );
}

// ─── OFFICE DIAGRAM ───────────────────────────────────────────────

const officeDevices = {
  backbone: {
    icon: "🔗", label: "KNX Backbone", model: "KNX Area Coupler",
    desc: "The KNX backbone connects multiple line segments via area couplers. Supports up to 15 areas × 15 lines × 255 devices = 57,375 devices maximum.",
    specs: [["Max areas", "15"], ["Lines per area", "15"], ["Devices per line", "255 (64 without extra PSU)"], ["Max total", "57,375 devices"]]
  },
  ipserver: {
    icon: "🖥️", label: "KNX IP Server", model: "KNX IP Router / Server",
    desc: "Central IP server for KNX building management. Enables remote access, visualization, BMS integration and multi-user ETS programming.",
    specs: [["Protocol", "KNXnet/IP"], ["Users", "Multi-user ETS access"], ["Integration", "BACnet, Modbus, DALI"], ["Remote", "VPN / cloud access"]]
  },
  bms: {
    icon: "🏢", label: "BMS Gateway", model: "KNX/BACnet Gateway",
    desc: "Bridges KNX with Building Management Systems (BMS) using BACnet, Modbus or OPC-UA protocols. Essential for commercial buildings.",
    specs: [["Protocols", "BACnet, Modbus, OPC-UA"], ["Data points", "Up to 1000+"], ["Integration", "HVAC, energy, access"], ["Reporting", "Real-time dashboard"]]
  },
  dali: {
    icon: "🔆", label: "DALI Gateway", model: "KNX/DALI Gateway",
    desc: "Integrates DALI lighting systems with KNX. Individual fixture control, dimming profiles, emergency lighting monitoring and scene management.",
    specs: [["DALI lines", "1-4 per gateway"], ["Fixtures", "64 per DALI line"], ["Functions", "Group, scene, dim"], ["Emergency", "Test & monitoring"]]
  },
  hvac: {
    icon: "❄️", label: "HVAC Controller", model: "KNX Room Controller",
    desc: "Controls heating, ventilation and air conditioning per zone. Fan coil unit control, VAV systems, underfloor heating and chilled ceiling integration.",
    specs: [["Zones", "Up to 255 per system"], ["Control", "PI/PID algorithm"], ["Systems", "FCU, VAV, UFH"], ["Setback", "Presence-based auto"]]
  },
  energymeter: {
    icon: "⚡", label: "Energy Meter", model: "KNX Energy Meter",
    desc: "Monitors electricity consumption per zone, floor or tenant. Essential for green building certification (LEED, BREEAM) and sub-metering.",
    specs: [["Measurement", "kWh, kW, A, V, PF"], ["Channels", "1-3 phase"], ["Reporting", "Daily/monthly logs"], ["Certification", "LEED/BREEAM ready"]]
  },
  s13: {
    icon: "📱", label: "Smart Touch S13", model: "GVS KNX Smart Touch S13",
    desc: "13-inch commercial grade KNX touch panel for office reception, meeting rooms and common areas. Full building control visualization.",
    specs: [["Display", "13\" HD capacitive"], ["Resolution", "1920×1080"], ["Functions", "Lighting, HVAC, blinds, access"], ["Mounting", "Flush or surface"]]
  },
  pushbutton: {
    icon: "🔘", label: "S Series Panel", model: "GVS KNX S Series Push Button",
    desc: "Commercial KNX push button panel for office workstations, meeting rooms and corridors. Clean design with LED status indication.",
    specs: [["Gangs", "1/2/3-gang"], ["LED", "Status indication"], ["Functions", "Light, blind, HVAC, scene"], ["Design", "Flush mount, slim profile"]]
  },
  presence: {
    icon: "👁️", label: "Presence Sensor", model: "GVS KNX Presence Sensor Microwave",
    desc: "High-sensitivity microwave presence detector for open-plan offices. Detects occupancy even with minimal movement — ideal for seated workers.",
    specs: [["Technology", "Microwave + PIR"], ["Coverage", "360° / 8m diameter"], ["Functions", "Presence, brightness"], ["Application", "Open plan offices"]]
  },
  co2: {
    icon: "🌬️", label: "CO₂/Temp/Humidity", model: "GVS KNX CO₂/Temp/Humidity Sensor",
    desc: "Monitors indoor air quality for demand-controlled ventilation. CO₂ levels trigger automatic fresh air supply, improving occupant wellbeing and productivity.",
    specs: [["CO₂ range", "0-5000ppm"], ["Temperature", "±0.5°C accuracy"], ["Humidity", "±3% RH"], ["Output", "KNX + optional analog"]]
  },
  blind: {
    icon: "🪟", label: "Blind Actuator", model: "KNX Blind Actuator",
    desc: "Motorized blind and shutter control with glare protection and solar tracking. Integrates with weather station for wind and rain safety cutoff.",
    specs: [["Channels", "4×2 outputs"], ["Solar tracking", "Automatic slat adjustment"], ["Weather safety", "Wind/rain cutoff"], ["Scenes", "Up to 64 scene positions"]]
  },
  access: {
    icon: "🚪", label: "Access Control", model: "GVS KNX Smart Touch V50s",
    desc: "Video door station integration with KNX. Door release, visitor recording, and corridor call functions integrated with building automation.",
    specs: [["Video", "HD camera, night vision"], ["Functions", "Door release, call, record"], ["Integration", "Full KNX events"], ["Floors", "Multi-floor intercoms"]]
  },
};

function OfficeDiagram() {
  const [active, setActive] = useState(null);
  const [activeFloor, setActiveFloor] = useState(1);
  const [view, setView] = useState("interactive");
  const toggle = (id) => setActive(active === id ? null : id);

  const floors = [
    {
      id: 1, label: "Ground Floor — Reception & Lobby",
      color: TEAL,
      zones: [
        { label: "Reception", devices: [{ id: "s13", icon: "📱", label: "Touch S13" }, { id: "access", icon: "🚪", label: "V50s Access" }, { id: "presence", icon: "👁️", label: "Presence" }] },
        { label: "Lobby / Common", devices: [{ id: "dali", icon: "🔆", label: "DALI GW" }, { id: "blind", icon: "🪟", label: "Blind Act." }, { id: "co2", icon: "🌬️", label: "CO₂ Sensor" }] },
      ]
    },
    {
      id: 2, label: "Floor 2 — Open Plan Office",
      color: PURPLE,
      zones: [
        { label: "Open Plan", devices: [{ id: "presence", icon: "👁️", label: "Presence" }, { id: "dali", icon: "🔆", label: "DALI GW" }, { id: "co2", icon: "🌬️", label: "CO₂ Sensor" }] },
        { label: "Meeting Rooms", devices: [{ id: "s13", icon: "📱", label: "Touch S13" }, { id: "blind", icon: "🪟", label: "Blind Act." }, { id: "hvac", icon: "❄️", label: "HVAC Ctrl" }] },
      ]
    },
    {
      id: 3, label: "Floor 3 — Executive Offices",
      color: AMBER,
      zones: [
        { label: "Private Offices", devices: [{ id: "s13", icon: "📱", label: "Touch S13" }, { id: "hvac", icon: "❄️", label: "HVAC Ctrl" }, { id: "blind", icon: "🪟", label: "Blind Act." }] },
        { label: "Board Room", devices: [{ id: "pushbutton", icon: "🔘", label: "S Series" }, { id: "dali", icon: "🔆", label: "DALI GW" }, { id: "presence", icon: "👁️", label: "Presence" }] },
      ]
    },
  ];

  const currentFloor = floors.find(f => f.id === activeFloor);

  return (
    <div>
      <div style={{ display:"flex", gap:"8px", marginBottom:"14px" }}>
        {["interactive","schematic"].map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            padding:"6px 16px", borderRadius:"6px", fontSize:"12px", fontWeight: view===v ? 700 : 500,
            background: view===v ? "#F97316" : "#1e3560",
            color: view===v ? "#fff" : "#94a3b8",
            border: `1px solid ${view===v ? "#F97316" : "#253d6e"}`,
            cursor:"pointer", textTransform:"capitalize",
          }}>{v}</button>
        ))}
      </div>
      {view === "interactive" && (
      <>
      {/* Building management core */}
      <div style={{
        background: NAVY_MID, border: `1px solid ${BORDER}`,
        borderRadius: "12px", padding: "16px 20px",
        marginBottom: "16px",
      }}>
        <div style={{ fontSize: "11px", color: ORANGE, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: "12px" }}>
          Building Management Core — Basement / Server Room
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
          {["backbone", "ipserver", "bms", "energymeter"].map(id => (
            <DeviceCard key={id} {...officeDevices[id]} active={active === id} onClick={() => toggle(id)} small />
          ))}
          <div style={{ flex: 1, height: "4px", background: `linear-gradient(90deg, ${ORANGE}, ${ORANGE}40)`, borderRadius: "2px", minWidth: "40px", boxShadow: `0 0 8px ${ORANGE}40` }} />
          <div style={{ fontSize: "10px", color: ORANGE, fontWeight: 700, whiteSpace: "nowrap" }}>KNX BACKBONE</div>
        </div>
      </div>

      {/* Floor selector */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
        {floors.map(f => (
          <button key={f.id} onClick={() => setActiveFloor(f.id)} style={{
            padding: "8px 16px", borderRadius: "8px", border: `2px solid ${activeFloor === f.id ? f.color : BORDER}`,
            background: activeFloor === f.id ? f.color + "22" : NAVY_LIGHT,
            color: activeFloor === f.id ? f.color : GRAY,
            fontWeight: activeFloor === f.id ? 700 : 500,
            fontSize: "12px", cursor: "pointer", transition: "all 0.15s",
          }}>Floor {f.id}</button>
        ))}
      </div>

      {/* Floor detail */}
      {currentFloor && (
        <div style={{
          background: NAVY_LIGHT, border: `1px solid ${currentFloor.color}40`,
          borderTop: `3px solid ${currentFloor.color}`,
          borderRadius: "10px", padding: "16px",
        }}>
          <div style={{ fontSize: "12px", fontWeight: 700, color: currentFloor.color, marginBottom: "14px", textTransform: "uppercase", letterSpacing: "1px" }}>
            {currentFloor.label}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            {currentFloor.zones.map(zone => (
              <div key={zone.label} style={{
                background: NAVY_MID, borderRadius: "8px", padding: "12px",
                border: `1px solid ${BORDER}`,
              }}>
                <div style={{ fontSize: "10px", color: GRAY, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>{zone.label}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {zone.devices.map(d => (
                    <DeviceCard key={d.id}
                      icon={d.icon} label={d.label}
                      color={currentFloor.color}
                      active={active === d.id}
                      onClick={() => toggle(d.id)}
                      small
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active && <InfoPanel device={officeDevices[active]} onClose={() => setActive(null)} />}
      {active && <div onClick={() => setActive(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999 }} />}
      </>
      )}
      {view === "schematic" && (
        <svg width="100%" viewBox="0 0 680 820" style={{display:"block"}}>
          <rect x="0" y="0" width="680" height="820" fill="#f1f5f9"/>
          <defs>
            <marker id="arrow2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </marker>
          </defs>
          <text x="340" y="30" textAnchor="middle" style={{fontSize:"13px",fontWeight:600,fill:"#0f172a"}}>KNX office building — area/line topology</text>
          <text x="340" y="48" textAnchor="middle" style={{fontSize:"11px",fill:"#64748b"}}>Multi-floor commercial · BACnet/DALI integration · ETS6</text>
          {/* Backbone spine */}
          <line x1="340" y1="62" x2="340" y2="760" stroke="#BA7517" strokeWidth="3" strokeLinecap="round"/>
          <text x="348" y="194" style={{fontSize:"10px",fontWeight:700,fill:"#BA7517"}}>KNX backbone</text>
          {/* Server room */}
          <rect x="40" y="60" width="590" height="130" rx="10" fill="none" stroke="#5F5E5A" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="52" y="76" style={{fontSize:"10px",fontWeight:600,fill:"#5F5E5A"}}>Server room / basement</text>
          <line x1="60" y1="120" x2="610" y2="120" stroke="#BA7517" strokeWidth="2.5"/>
          <rect x="52" y="104" width="96" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="100" y="122" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">PSU 640mA</text>
          <text x="100" y="138" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Main supply</text>
          <line x1="100" y1="104" x2="100" y2="120" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="170" y="104" width="96" height="44" rx="6" fill="#ffffff" stroke="#378ADD" strokeWidth="1.5"/>
          <text x="218" y="122" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">IP server</text>
          <text x="218" y="138" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">KNXnet/IP</text>
          <line x1="218" y1="104" x2="218" y2="120" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="288" y="104" width="108" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="342" y="122" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">BMS gateway</text>
          <text x="342" y="138" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">BACnet/Modbus</text>
          <line x1="342" y1="104" x2="342" y2="120" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="418" y="104" width="104" height="44" rx="6" fill="#ffffff" stroke="#3B6D11" strokeWidth="1.5"/>
          <text x="470" y="122" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Energy meter</text>
          <text x="470" y="138" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">kWh / 3-phase</text>
          <line x1="470" y1="104" x2="470" y2="120" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="540" y="104" width="104" height="44" rx="6" fill="#ffffff" stroke="#5F5E5A" strokeWidth="1.5"/>
          <text x="592" y="122" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Area coupler</text>
          <text x="592" y="138" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Area 1</text>
          <line x1="592" y1="104" x2="592" y2="120" stroke="#BA7517" strokeWidth="1.5"/>
          {/* Floor 1 */}
          <rect x="40" y="222" width="590" height="160" rx="10" fill="none" stroke="#0F6E56" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="52" y="238" style={{fontSize:"10px",fontWeight:600,fill:"#0F6E56"}}>Floor 1 — Reception &amp; lobby</text>
          <rect x="310" y="248" width="60" height="36" rx="6" fill="#ffffff" stroke="#5F5E5A" strokeWidth="1.5"/>
          <text x="340" y="267" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">LC F1</text>
          <line x1="340" y1="248" x2="340" y2="220" stroke="#BA7517" strokeWidth="1.5"/>
          <line x1="60" y1="290" x2="610" y2="290" stroke="#BA7517" strokeWidth="1.5"/>
          <line x1="340" y1="284" x2="340" y2="290" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="52" y="304" width="100" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="102" y="322" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Touch S13</text>
          <text x="102" y="338" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">13" reception</text>
          <line x1="102" y1="304" x2="102" y2="290" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="164" y="304" width="96" height="44" rx="6" fill="#ffffff" stroke="#3B6D11" strokeWidth="1.5"/>
          <text x="212" y="322" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Access ctrl</text>
          <text x="212" y="338" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">V50s intercom</text>
          <line x1="212" y1="304" x2="212" y2="290" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="272" y="304" width="96" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="320" y="322" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">DALI gateway</text>
          <text x="320" y="338" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">64 fixtures</text>
          <line x1="320" y1="304" x2="320" y2="290" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="380" y="304" width="96" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="428" y="322" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">CO₂ sensor</text>
          <text x="428" y="338" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">IAQ / ventilation</text>
          <line x1="428" y1="304" x2="428" y2="290" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="488" y="304" width="116" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="546" y="322" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Blind act.</text>
          <text x="546" y="338" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Solar protect</text>
          <line x1="546" y1="304" x2="546" y2="290" stroke="#BA7517" strokeWidth="1.2"/>
          {/* Floor 2 */}
          <rect x="40" y="414" width="590" height="160" rx="10" fill="none" stroke="#534AB7" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="52" y="430" style={{fontSize:"10px",fontWeight:600,fill:"#534AB7"}}>Floor 2 — Open plan office</text>
          <rect x="310" y="440" width="60" height="36" rx="6" fill="#ffffff" stroke="#5F5E5A" strokeWidth="1.5"/>
          <text x="340" y="459" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">LC F2</text>
          <line x1="340" y1="440" x2="340" y2="412" stroke="#BA7517" strokeWidth="1.5"/>
          <line x1="60" y1="482" x2="610" y2="482" stroke="#BA7517" strokeWidth="1.5"/>
          <line x1="340" y1="476" x2="340" y2="482" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="52" y="496" width="104" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="104" y="514" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Presence sensor</text>
          <text x="104" y="530" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Microwave 360°</text>
          <line x1="104" y1="496" x2="104" y2="482" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="168" y="496" width="96" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="216" y="514" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">DALI gateway</text>
          <text x="216" y="530" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Open plan lights</text>
          <line x1="216" y1="496" x2="216" y2="482" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="276" y="496" width="96" height="44" rx="6" fill="#ffffff" stroke="#0F6E56" strokeWidth="1.5"/>
          <text x="324" y="514" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">CO₂ sensor</text>
          <text x="324" y="530" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Demand ventilation</text>
          <line x1="324" y1="496" x2="324" y2="482" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="384" y="496" width="96" height="44" rx="6" fill="#ffffff" stroke="#378ADD" strokeWidth="1.5"/>
          <text x="432" y="514" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Touch S13</text>
          <text x="432" y="530" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Meeting room</text>
          <line x1="432" y1="496" x2="432" y2="482" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="492" y="496" width="96" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="540" y="514" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">HVAC ctrl</text>
          <text x="540" y="530" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">FCU/VAV zones</text>
          <line x1="540" y1="496" x2="540" y2="482" stroke="#BA7517" strokeWidth="1.2"/>
          {/* Floor 3 */}
          <rect x="40" y="606" width="590" height="160" rx="10" fill="none" stroke="#854F0B" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="52" y="622" style={{fontSize:"10px",fontWeight:600,fill:"#854F0B"}}>Floor 3 — Executive offices</text>
          <rect x="310" y="632" width="60" height="36" rx="6" fill="#ffffff" stroke="#5F5E5A" strokeWidth="1.5"/>
          <text x="340" y="651" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">LC F3</text>
          <line x1="340" y1="632" x2="340" y2="604" stroke="#BA7517" strokeWidth="1.5"/>
          <line x1="60" y1="674" x2="610" y2="674" stroke="#BA7517" strokeWidth="1.5"/>
          <line x1="340" y1="668" x2="340" y2="674" stroke="#BA7517" strokeWidth="1.5"/>
          <rect x="52" y="688" width="96" height="44" rx="6" fill="#ffffff" stroke="#378ADD" strokeWidth="1.5"/>
          <text x="100" y="706" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Touch S13</text>
          <text x="100" y="722" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Exec offices</text>
          <line x1="100" y1="688" x2="100" y2="674" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="160" y="688" width="88" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="204" y="706" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">HVAC ctrl</text>
          <text x="204" y="722" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Per-office zone</text>
          <line x1="204" y1="688" x2="204" y2="674" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="260" y="688" width="108" height="44" rx="6" fill="#ffffff" stroke="#BA7517" strokeWidth="1.5"/>
          <text x="314" y="706" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">Blind act.</text>
          <text x="314" y="722" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Glare protection</text>
          <line x1="314" y1="688" x2="314" y2="674" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="380" y="688" width="96" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="428" y="706" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">S-series panel</text>
          <text x="428" y="722" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Board room</text>
          <line x1="428" y1="688" x2="428" y2="674" stroke="#BA7517" strokeWidth="1.2"/>
          <rect x="488" y="688" width="96" height="44" rx="6" fill="#ffffff" stroke="#7c3aed" strokeWidth="1.5"/>
          <text x="536" y="706" textAnchor="middle" style={{fontSize:"11px",fontWeight:700,fill:"#1e293b"}} dominantBaseline="central">DALI gateway</text>
          <text x="536" y="722" textAnchor="middle" style={{fontSize:"10px",fill:"#64748b"}} dominantBaseline="central">Scene control</text>
          <line x1="536" y1="688" x2="536" y2="674" stroke="#BA7517" strokeWidth="1.2"/>
          {/* Legend */}
          <rect x="40" y="766" width="600" height="40" rx="8" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.5"/>
          <rect x="56" y="779" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#0F6E56"/>
          <text x="72" y="788" style={{fontSize:"10px",fill:"#64748b"}}>Sensors</text>
          <rect x="140" y="779" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#BA7517"/>
          <text x="156" y="788" style={{fontSize:"10px",fill:"#64748b"}}>Actuators</text>
          <rect x="234" y="779" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#7c3aed"/>
          <text x="250" y="788" style={{fontSize:"10px",fill:"#64748b"}}>Lighting (DALI)</text>
          <rect x="358" y="779" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#378ADD"/>
          <text x="374" y="788" style={{fontSize:"10px",fill:"#64748b"}}>Displays</text>
          <rect x="440" y="779" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#5F5E5A"/>
          <text x="456" y="788" style={{fontSize:"10px",fill:"#64748b"}}>Couplers</text>
          <rect x="530" y="779" width="10" height="10" rx="2" fill="#e2e8f0" stroke="#3B6D11"/>
          <text x="546" y="788" style={{fontSize:"10px",fill:"#64748b"}}>Infrastructure</text>
        </svg>
      )}
    </div>
  );
}

// ─── MAIN EXPORT ─────────────────────────────────────────────────

export default function KNXDiagrams() {
  const [tab, setTab] = useState("residential");

  return (
    <div style={{
      minHeight: "100vh",
      background: SURFACE,
      fontFamily: "'Segoe UI', sans-serif",
      color: "#c0cce0",
    }}>
      {/* Header */}
      <div style={{
        background: NAVY, borderBottom: `2px solid ${ORANGE}`,
        padding: "14px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "36px", height: "36px", background: ORANGE,
            borderRadius: "8px", display: "flex", alignItems: "center",
            justifyContent: "center", fontWeight: 900, color: "#fff", fontSize: "13px",
          }}>BB</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: "15px", color: "#f0f4ff" }}>Briq-n-Braq</div>
            <div style={{ fontSize: "9px", color: GRAY, letterSpacing: "1.5px", textTransform: "uppercase" }}>KNX System Diagrams</div>
          </div>
        </div>
        <div style={{ fontSize: "10px", color: ORANGE, fontWeight: 700, letterSpacing: "1px" }}>
          ✦ KNX Partner No. 20248
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        background: NAVY_LIGHT, borderBottom: `1px solid ${BORDER}`,
        padding: "0 24px", display: "flex", gap: "4px",
      }}>
        {[
          { id: "residential", label: "🏠 Residential KNX System" },
          { id: "office", label: "🏢 Office Building Automation" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "12px 20px", background: "none",
            border: "none", borderBottom: tab === t.id ? `3px solid ${ORANGE}` : "3px solid transparent",
            color: tab === t.id ? ORANGE : GRAY,
            fontWeight: tab === t.id ? 700 : 500,
            fontSize: "13px", cursor: "pointer",
            transition: "all 0.15s",
          }}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: "24px", maxWidth: "960px", margin: "0 auto" }}>

        {tab === "residential" && (
          <div>
            <div style={{ marginBottom: "20px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 900, color: "#f0f4ff", margin: "0 0 6px" }}>
                Residential KNX System
              </h2>
              <p style={{ color: GRAY, fontSize: "12px", margin: 0 }}>
                Typical villa or apartment installation · Click any device to see specifications
              </p>
            </div>
            <ResidentialDiagram />
          </div>
        )}

        {tab === "office" && (
          <div>
            <div style={{ marginBottom: "20px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 900, color: "#f0f4ff", margin: "0 0 6px" }}>
                Office Building Automation
              </h2>
              <p style={{ color: GRAY, fontSize: "12px", margin: 0 }}>
                Multi-floor commercial building · Select a floor to explore · Click any device for details
              </p>
            </div>
            <OfficeDiagram />
          </div>
        )}

        {/* Legend */}
        <div style={{
          marginTop: "24px", background: NAVY_LIGHT,
          border: `1px solid ${BORDER}`, borderRadius: "10px",
          padding: "14px 18px",
          display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center",
        }}>
          <div style={{ fontSize: "10px", color: GRAY, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px" }}>Legend:</div>
          {[
            { color: ORANGE, label: "KNX TP Bus (29V DC)" },
            { color: TEAL, label: "Sensors & Input devices" },
            { color: PURPLE, label: "Control panels & displays" },
            { color: AMBER, label: "Actuators & outputs" },
            { color: GREEN, label: "System & infrastructure" },
          ].map(l => (
            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: l.color }} />
              <span style={{ fontSize: "10px", color: "#94a3b8" }}>{l.label}</span>
            </div>
          ))}
          <div style={{ marginLeft: "auto", fontSize: "10px", color: GRAY }}>
            ETS6 · KNX TP · KNXnet/IP · IEC 14543-3
          </div>
        </div>
      </div>
    </div>
  );
}
